import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { END_POINT, getJwtToken } from '../../Redux/AdminReducer/action';

// Where the board kept its stars before the selection moved server side. Read
// once, pushed to the clinic's preferences if none are saved yet, then dropped.
const LEGACY_PINNED_KEY = 'graphs.pinnedCards';

const authConfig = () => {
  const token = getJwtToken();
  return token ? { headers: { Authorization: token } } : {};
};

const readError = (error, fallback) =>
  error?.response?.data?.message || error?.response?.data?.error || error?.message || fallback;

const cleanIds = (value) =>
  Array.isArray(value) ? [...new Set(value.filter((id) => typeof id === 'string' && id))] : [];

const readLegacyPins = () => {
  try {
    return cleanIds(JSON.parse(window.localStorage.getItem(LEGACY_PINNED_KEY)));
  } catch {
    return [];
  }
};

const dropLegacyPins = () => {
  try {
    window.localStorage.removeItem(LEGACY_PINNED_KEY);
  } catch {
    /* storage unavailable - nothing to drop */
  }
};

// Each list is saved on its own: the server keeps whichever list is left out.
const saveSelection = async (ids, field = 'dashboardCards') => {
  const { data } = await axios.put(`${END_POINT}/graph-preferences`, { [field]: ids }, authConfig());
  return cleanIds(data?.[field]);
};

/**
 * The clinic's "Show on dashboard" selection: graph card ids, stored per tenant
 * via /graph-preferences so every login on the clinic sees the same dashboard.
 * Toggles are optimistic and roll back if the save fails.
 */
export default function useGraphPreferences() {
  return useCardSelection('dashboardCards', { migrateLegacy: true, label: 'dashboard' });
}

/**
 * The clinic's "Show on website" selection (`websiteCards`). Only the graphs in
 * `eligible` - aggregate counts whose labels never name a person - can be
 * published; the server enforces the same list and the `max` cap.
 */
export function useWebsiteGraphPreferences() {
  return useCardSelection('websiteCards', { label: 'website' });
}

function useCardSelection(field, { migrateLegacy = false, label = 'dashboard' } = {}) {
  const [eligible, setEligible] = useState(null);
  const [max, setMax] = useState(0);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [status, setStatus] = useState(0);
  const latest = useRef([]);
  const saveSeq = useRef(0);

  const apply = useCallback((ids) => {
    latest.current = ids;
    setSelected(ids);
  }, []);

  useEffect(() => {
    let cancelled = false;

    axios
      .get(`${END_POINT}/graph-preferences`, authConfig())
      .then(async ({ data }) => {
        if (cancelled) return;
        setStatus(200);
        if (Array.isArray(data?.websiteEligible)) setEligible(cleanIds(data.websiteEligible));
        setMax(Number(data?.maxWebsiteCards) || 0);
        const stored = cleanIds(data?.[field]);
        const legacy = migrateLegacy ? readLegacyPins() : [];

        if (!data?.saved && legacy.length) {
          apply(legacy);
          try {
            const saved = await saveSelection(legacy, field);
            dropLegacyPins();
            if (!cancelled) apply(saved);
          } catch {
            /* keep the local pins; the next visit retries the migration */
          }
          return;
        }

        if (data?.saved && migrateLegacy) dropLegacyPins();
        apply(stored);
      })
      .catch((err) => {
        if (cancelled) return;
        setStatus(err?.response?.status || 0);
        setError(readError(err, `Could not load ${label} graphs`));
        apply([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [apply, field, migrateLegacy, label]);

  const setOnDashboard = useCallback(
    (id, checked) => {
      const previous = latest.current;
      const has = previous.includes(id);
      if (checked === has) return;
      const next = checked ? [...previous, id] : previous.filter((item) => item !== id);

      apply(next);
      setError('');
      setSaving(true);
      const seq = ++saveSeq.current;

      saveSelection(next, field)
        .then((saved) => {
          // A later toggle owns the state now; only the newest save may settle it.
          if (seq === saveSeq.current) apply(saved);
        })
        .catch((err) => {
          if (seq !== saveSeq.current) return;
          apply(previous);
          setError(readError(err, `Could not save the ${label} selection`));
        })
        .finally(() => {
          if (seq === saveSeq.current) setSaving(false);
        });
    },
    [apply, field, label],
  );

  /**
   * Drops ticked ids that no longer exist (a metric renamed or removed on the
   * server). Saves only when something was actually dropped.
   */
  const retainOnly = useCallback(
    (isValid) => {
      const previous = latest.current;
      const next = previous.filter((id) => isValid(id));
      if (next.length === previous.length) return;
      apply(next);
      const seq = ++saveSeq.current;
      saveSelection(next, field)
        .then((saved) => {
          if (seq === saveSeq.current) apply(saved);
        })
        .catch(() => {
          /* harmless: the stale ids are filtered out again on the next visit */
        });
    },
    [apply, field],
  );

  const toggle = useCallback((id) => setOnDashboard(id, !latest.current.includes(id)), [setOnDashboard]);

  return { selected, loading, saving, error, status, eligible, max, setOnDashboard, toggle, retainOnly };
}
